import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PointsSummaryFacade } from '@features/points-summary/facades/points-summary-facade/points-summary.facade';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { getAuthIsLoggedIn } from '@rd/core/auth/store/selectors/auth.selectors';
import { LocaleFacade } from '@rd/core/facades/locale-facade/locale.facade';
import { getLoyaltyCurrencySelected } from '@rd/core/loyalty-currency/store/selectors/loyalty-currency.selectors';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { PageTitleService } from '@rd/core/services/page-title/page-title.service';
import { CoreState } from '@rd/core/store/reducers';
import { LoyaltyProgramHelper } from '@rd/helpers/loyalty-program.helper';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { PopupSettingService } from '@rd/popup/services/popup-setting/popup-setting.service';
import { CheckoutErrorDialogComponent } from '@rd/shared-legacy/components/checkout-error-dialog/checkout-error-dialog.component';
import {
  HttpError,
  LoyaltyCurrency,
  LoyaltyProgram,
  PointsTransferMembership,
} from '@rd/types';
import { Observable, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  first,
  skip,
  take,
  takeUntil,
} from 'rxjs/operators';
import {
  CheckoutLinkedAccountPosition,
  TransferConnectV2SettingService,
} from '../../services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';
import {
  LoadMemberships,
  LoadSingleLoyaltyProgram,
  SubmitCheckoutFormData,
} from '../../store/actions';
import { TransferConnectV2State } from '../../store/reducers';
import { getCheckoutFormData } from '../../store/selectors/checkout.selectors';
import {
  getSingleLoyaltyProgram,
  getSingleLoyaltyProgramError,
  getSingleLoyaltyProgramLoading,
} from '../../store/selectors/loyalty-program.selectors';
import { getMembershipsOrUpdateMembershipIsLoading } from '../../store/selectors/membership.selectors';
import {
  getLoyaltyProgramMembership,
  getSingleLoyaltyProgramOrMembershipsAreLoading,
} from '../../store/selectors/transfer-connect-combined.selectors';
import { TransferConnectV2CheckoutFormData } from '../../types';

@Component({
  selector: 'rd-tcv2-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TcV2CheckoutComponent implements OnInit, OnDestroy {
  @HostBinding('class.rd-tcv2-checkout') componentClass = true;

  loyaltyCurrencySelected: LoyaltyCurrency;
  loyaltyProgram$: Observable<LoyaltyProgram>;
  loyaltyProgramOrMembershipsLoading$: Observable<boolean>;
  loyaltyProgramMembership$: Observable<PointsTransferMembership>;
  loyaltyProgramMembership: PointsTransferMembership;
  loyaltyProgramLoading$: Observable<boolean>;
  membershipsOrUpdateMembershipIsLoading$: Observable<boolean>;

  collapsibleTransferAmountForm: boolean;
  isLoggedIn: boolean;
  linkedAccountPosition: CheckoutLinkedAccountPosition;
  loyaltyProgram: LoyaltyProgram;
  shouldRenderCheckoutBanner: boolean;
  shouldRenderCheckoutBreadcrumb: boolean;
  shouldRenderAdditionalMessage: boolean;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  loyaltyProgramId: string;
  // @ts-ignore
  checkoutFormData$: Observable<TransferConnectV2CheckoutFormData>;

  private destroy$ = new Subject();

  constructor(
    protected store: Store<{
      core: CoreState;
      transferConnectV2: TransferConnectV2State;
    }>,
    protected dialogService: DialogService,
    protected loyaltyProgramHelper: LoyaltyProgramHelper,
    protected route: ActivatedRoute,
    protected router: Router,
    protected translateService: TranslateService,
    protected analyticsService: AnalyticsService,
    protected popupSettingService: PopupSettingService,
    protected pointsSummaryFacade: PointsSummaryFacade,
    protected pageTitleService: PageTitleService,
    protected localeFacade: LocaleFacade,
    protected transferConnectV2SettingService: TransferConnectV2SettingService,
  ) {}

  ngOnInit(): void {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.loyaltyProgramId = this.route.snapshot.paramMap.get('programId')!;
    // @ts-ignore
    this.checkoutFormData$ = this.store.select(getCheckoutFormData, {
      id: this.loyaltyProgramId,
    });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.loyaltyProgramLoading$ = this.store.select(
      getSingleLoyaltyProgramLoading,
    );
    this.loyaltyProgramOrMembershipsLoading$ = this.store.select(
      getSingleLoyaltyProgramOrMembershipsAreLoading,
    );
    this.membershipsOrUpdateMembershipIsLoading$ = this.store.select(
      getMembershipsOrUpdateMembershipIsLoading,
    );

    this.collapsibleTransferAmountForm =
      this.transferConnectV2SettingService.collapsibleTransferAmountForm;
    this.linkedAccountPosition =
      this.transferConnectV2SettingService.linkedAccountPosition;
    this.shouldRenderCheckoutBanner =
      this.transferConnectV2SettingService.shouldRenderCheckoutBanner;
    this.shouldRenderCheckoutBreadcrumb =
      this.transferConnectV2SettingService.shouldRenderCheckoutBreadcrumb;
    this.shouldRenderAdditionalMessage =
      !!this.transferConnectV2SettingService.checkoutPageText
        .additionalCheckoutTitle ||
      !!this.transferConnectV2SettingService.checkoutPageText
        .additionalCheckoutMessage;

    this.assignIsLoggedInAndMemberships();
    this.assignLoyaltyCurrency();
    this.assignLoyaltyProgram();
    this.assignLoyaltyProgramMembership();

    this.redirectToHomepageIfLoyaltyProgramDoesntExist();
    this.pageTitleService.setPageTitle('transfer-connect-checkout');

    this.analyticsService.track({
      // @ts-ignore
      routeName: this.analyticsService.sitemap.transferConnect.checkout,
    });

    this.store
      .select(getSingleLoyaltyProgram, { id: this.loyaltyProgramId })
      .pipe(
        first((loyaltyProgram) => !!loyaltyProgram),
        takeUntil(this.destroy$),
      )
      .subscribe((loyaltyProgram) => {
        if (loyaltyProgram) {
          this.analyticsService.track({
            routeName:
              // @ts-ignore
              this.analyticsService.sitemap.transferConnect.checkoutFullData,
            extra: { brand: loyaltyProgram.name },
          });
        }
      });

    // Skip the first locale "change" - this gets emitted when the page loads, not when the locale is actually changed
    this.localeFacade.selectedLocale$
      .pipe(takeUntil(this.destroy$), skip(1))
      .subscribe(() => {
        this.loadSingleLoyaltyProgram();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  submitCheckoutFormData(
    formData: Partial<TransferConnectV2CheckoutFormData>,
  ): void {
    // check if formData.transfer amount > store.
    this.pointsSummaryFacade.pointsSummary$
      .pipe(take(1))
      // @ts-ignore
      .subscribe(({ pointsBalance }) => {
        // @ts-ignore
        if (formData.sourceAmount > pointsBalance) {
          const loyaltyCurrency =
            this.loyaltyCurrencySelected[
              this.transferConnectV2SettingService
                .insufficientMilesCurrencyNameAttribute
            ];

          this.dialogService.open(CheckoutErrorDialogComponent, {
            data: {
              ctaButtonText: this.translateService.instant('modal.error.ok'),
              errorMessageDescription: this.translateService.instant(
                'fulfillment.insufficient_miles',
                { currencyShortName: loyaltyCurrency },
              ),
              errorMessageTitle: this.translateService.instant(
                'fulfillment.insufficient_miles_title',
                { loyaltyCurrency },
              ),
              iconName: this.popupSettingService.insufficientMilesIconType,
            },
            id: 'rd-shared-checkout-error-dialog',
            screenMode: 'center-md',
            ariaLabel: this.translateService.instant(
              'fulfillment.insufficient_miles_title',
              { loyaltyCurrency },
            ),
          });
        } else {
          this.store.dispatch(
            new SubmitCheckoutFormData({
              loyaltyProgramId: this.loyaltyProgram.id,
              formData: {
                ...formData,
                membershipId: this.loyaltyProgramMembership.id,
              } as TransferConnectV2CheckoutFormData,
            }),
          );

          this.router.navigate(['/transfer/review'], {
            queryParams: { loyaltyProgramId: this.loyaltyProgram.id },
          });
        }
      });
  }

  get shouldDisableForm(): boolean {
    return (
      this.transferConnectV2SettingService.collapsibleTransferAmountForm &&
      !this.loyaltyProgramMembership
    );
  }

  private redirectToHomepageIfLoyaltyProgramDoesntExist(): void {
    this.store
      .select(getSingleLoyaltyProgramError)
      .pipe(takeUntil(this.destroy$))
      .subscribe((httpError) => {
        if (httpError && this.loyaltyProgramNotFound(httpError)) {
          this.router.navigate(['/']);
        }
      });
  }

  // Method called when the page is loaded. Only call NN to load the loyalty program (LP)
  // if the translations haven't been loaded yet, i.e. the user hasn't come to this page for this LP yet.
  // TC listing => TC checkout = load LP.
  // TC listing => TC checkout (LP loaded) => TC listing => TC checkout = no need to load LP.
  private assignLoyaltyProgram(): void {
    this.loyaltyProgram$ = this.store.select(getSingleLoyaltyProgram, {
      id: this.loyaltyProgramId,
    });
    this.store
      .select(getSingleLoyaltyProgram, { id: this.loyaltyProgramId })
      .pipe(takeUntil(this.destroy$))
      .subscribe((loyaltyProgram) => {
        this.loyaltyProgram = loyaltyProgram;

        if (!this.loyaltyProgramHelper.isFullyLoaded(loyaltyProgram)) {
          this.loadSingleLoyaltyProgram();
        }
      });
  }

  // Method called when locale changes. Regardless if the loyalty program (LP) has been loaded before or not,
  // call NN again to fetch translations for that specific LP
  private loadSingleLoyaltyProgram(): void {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.store.dispatch(new LoadSingleLoyaltyProgram(this.loyaltyProgramId!));
  }

  private assignLoyaltyCurrency(): void {
    this.store
      .select(getLoyaltyCurrencySelected)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        // @ts-ignore
        (loyaltyCurrency) => (this.loyaltyCurrencySelected = loyaltyCurrency),
      );
  }

  private assignIsLoggedInAndMemberships(): void {
    this.store
      .select(getAuthIsLoggedIn)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((isLoggedIn) => {
        // @ts-ignore
        this.isLoggedIn = isLoggedIn;

        if (isLoggedIn) {
          this.store.dispatch(new LoadMemberships());
        }
      });
  }

  private assignLoyaltyProgramMembership(): void {
    // @ts-ignore
    this.loyaltyProgramMembership$ = this.store.select(
      getLoyaltyProgramMembership,
      { id: this.loyaltyProgramId },
    );
    this.store
      .select(getLoyaltyProgramMembership, { id: this.loyaltyProgramId })
      .pipe(takeUntil(this.destroy$))
      // @ts-ignore
      .subscribe((membership) => (this.loyaltyProgramMembership = membership));
  }

  private loyaltyProgramNotFound(httpError: HttpError): boolean {
    return httpError && httpError.status === 404;
  }
}
